/**
 * Centralized Google Ad Manager (GPT / gpt.js) lifecycle controller.
 *
 * Why this exists:
 * GPT ad slots on the homepage are injected between categories that load lazily
 * via infinite scroll. With Single Request Architecture (SRA), only the slots that
 * exist at the moment of the first ad request get filled; slots mounted later never
 * issue a request and render empty. The supported pattern for dynamically inserted
 * slots is: disable the initial load, then explicitly refresh() each slot as it mounts.
 *
 * This module is the single owner of that flow so callers only declare a slot
 * (divId + adUnitPath + sizes) and never touch googletag directly.
 */

let initialized = false;
// divId -> slot, so define/refresh/destroy stay idempotent across re-mounts.
const slots = new Map();

// Ensure the googletag command queue exists, then queue work on it.
function withGoogletag(fn) {
  if (typeof window === 'undefined') return;
  window.googletag = window.googletag || { cmd: [] };
  window.googletag.cmd.push(fn);
}

/**
 * One-time global GPT configuration. Idempotent — safe to call from multiple places.
 * Order matters: disableInitialLoad() and enableSingleRequest() must run before
 * enableServices(). disableInitialLoad() is what makes every slot (including the
 * first) wait for an explicit refresh() instead of fetching on display().
 */
export function initGpt() {
  if (initialized) return;
  initialized = true;

  withGoogletag(() => {
    const pubads = window.googletag.pubads();
    pubads.disableInitialLoad();
    pubads.enableSingleRequest();
    window.googletag.enableServices();
  });
}

/**
 * Define (if needed), display, and fetch a single slot.
 * - Guaranteed init before any define.
 * - Idempotent: re-registering an existing divId just refreshes it instead of
 *   creating a duplicate (defineSlot returns null on duplicate div ids).
 *
 * @param {{ divId: string, adUnitPath: string, sizes: (number[]|number[][]) }} config
 */
export function registerSlot({ divId, adUnitPath, sizes }) {
  if (!divId || !adUnitPath) return;
  initGpt();

  withGoogletag(() => {
    const existing = slots.get(divId);
    if (existing) {
      window.googletag.pubads().refresh([existing]);
      return;
    }

    const slot = window.googletag.defineSlot(adUnitPath, sizes, divId);
    if (!slot) return;

    slot.addService(window.googletag.pubads());
    slots.set(divId, slot);

    window.googletag.display(divId);
    // Required because disableInitialLoad() suppresses the fetch on display().
    window.googletag.pubads().refresh([slot]);
  });
}

/**
 * Tear down a slot on unmount so its div id can be safely reused later.
 */
export function destroySlot(divId) {
  if (!divId) return;

  withGoogletag(() => {
    const slot = slots.get(divId);
    if (!slot) return;
    window.googletag.destroySlots([slot]);
    slots.delete(divId);
  });
}
